import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Container, FloatingLabel, Form, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import LegalWarning from "../components/legalwarning";
import InsetAlert from "../components/insetalert";
import DotLoader from "../components/dotloader";
import PasskeyRecoveryCodes from "../components/passkey-recovery-codes";
import { useActivatePasskeyMutation } from "../new/auth.query";
import authStyles from "../styles/auth-pages.module.css";
import securityStyles from "../styles/passkey-security.module.css";

const defaultFriendlyName = "This device";

function ActivateAccount() {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const activationToken = searchParams.get("activationtoken") || "";
    const [friendlyName, setFriendlyName] = useState(defaultFriendlyName);
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const activateMutation = useActivatePasskeyMutation((session) => {
        setRecoveryCodes(session?.recoveryCodes ?? []);
    });

    useEffect(() => {
        document.title = "MFP - Activate Account";
    }, []);

    const handleActivate = async (event) => {
        event.preventDefault();

        try {
            await activateMutation.mutateAsync({
                activationToken,
                friendlyName: friendlyName?.trim() || defaultFriendlyName
            });
        } catch {
            // Expected activation failures are displayed by the mutation error state.
        }
    };

    return (
        <Container fluid className={authStyles.authViewport}>
            <div className={authStyles.authShell}>
                <div className={authStyles.formColumn}>
                    <Row className={authStyles.authRow}>
                        <Col xs={12} className={authStyles.authCol}>
                            <div className={authStyles.legalWrap}>
                                <div className={authStyles.legalDesktop}>
                                    <LegalWarning />
                                </div>
                            </div>
                            <Card className={authStyles.authCard}>
                                <Card.Img className={authStyles.banner} variant="top" src="../KTMAHEADER.png" alt="Money Follows The Person Banner" />
                                <Card.Body className={authStyles.cardBody}>
                                    <p className={securityStyles.eyebrow}>Account Activation</p>
                                    <h3 className={authStyles.title}>Create your passkey</h3>
                                    <p className={authStyles.subtitle}>
                                        Finish setting up your account by registering a passkey on a trusted device. This invite link is short-lived and can only be used once.
                                    </p>

                                    {!activationToken ? (
                                        <InsetAlert
                                            error={{ error: "The activation link is missing or invalid. Request a new invitation from an administrator." }}
                                            heading="Invalid Activation Link"
                                            style={{ width: "100%" }}
                                        />
                                    ) : null}
                                    {activateMutation.error ? (
                                        <InsetAlert error={{ error: activateMutation.error.message }} heading="Activation Failed" style={{ width: "100%" }} />
                                    ) : null}
                                    {activateMutation.data ? (
                                        <InsetAlert
                                            type="Success"
                                            heading="Passkey Ready"
                                            body="Your account has been activated. Save the recovery codes below before continuing."
                                            style={{ width: "100%" }}
                                        />
                                    ) : null}

                                    {activateMutation.data ? (
                                        <>
                                            <PasskeyRecoveryCodes codes={recoveryCodes} />
                                            <div className="d-flex flex-wrap gap-2 mt-4">
                                                <Button type="button" onClick={() => navigate("/", { replace: true })}>
                                                    Continue to application
                                                </Button>
                                            </div>
                                        </>
                                    ) : (
                                        <Form onSubmit={handleActivate}>
                                            <FloatingLabel controlId="activation-device-name" label="Device name" className="mb-3">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Device name"
                                                    value={friendlyName}
                                                    disabled={!activationToken || activateMutation.isPending}
                                                    onChange={(event) => setFriendlyName(event.target.value)}
                                                />
                                            </FloatingLabel>
                                            <div className={authStyles.actionRow}>
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    className="ps-0"
                                                    disabled={activateMutation.isPending}
                                                    onClick={() => navigate("/Auth/Login", { replace: true })}
                                                >
                                                    Back to login
                                                </Button>
                                                <Button type="submit" disabled={!activationToken || activateMutation.isPending}>
                                                    {activateMutation.isPending ? <DotLoader ButtonStyle={true} /> : "Create Passkey"}
                                                </Button>
                                            </div>
                                        </Form>
                                    )}
                                </Card.Body>
                            </Card>
                            <details className={authStyles.legalMobile}>
                                <summary className={authStyles.legalMobileSummary}>Legal Warning</summary>
                                <div className={authStyles.legalMobileBody}>
                                    <LegalWarning />
                                </div>
                            </details>
                        </Col>
                    </Row>
                </div>
            </div>
        </Container>
    );
}

export default ActivateAccount;
